import { Grid, Link, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'

import CTAButton from 'app/components/CTAButton'
import errorIcon from 'app/images/error-icon.svg'
import { FRESHCHAT_CHANNEL_NAME } from 'app/utils/constants'

const figEmail = 'support@fig.ca'

function ContactFigLink({ screen, analyticsCallback }: { screen: string; analyticsCallback: (object: string, page_name: string) => void }) {
    const { t } = useTranslation()
    const contactFigLabel = t('PleaseReachOut.contactFigLabel')
    return (
        <Link
            href={`mailto:${figEmail}`}
            target="_blank"
            whiteSpace="nowrap"
            onClick={() => {
                analyticsCallback(contactFigLabel, screen)
            }}
            sx={{ cursor: 'pointer' }}
        >
            {contactFigLabel}
        </Link>
    )
}

const openChat: () => void = () => window.fcWidget?.open({ name: FRESHCHAT_CHANNEL_NAME })

function LiveChatLink({ screen, analyticsCallback }: { screen: string; analyticsCallback: (object: string, page_name: string) => void }) {
    const { t } = useTranslation()
    const figChatLabel = t('PleaseReachOut.liveChatLabel')
    const theme = useTheme()
    return (
        <Link
            sx={{ whiteSpace: 'nowrap', color: theme.color.figGreen, cursor: 'pointer' }}
            onClick={() => {
                analyticsCallback(figChatLabel, screen)
                openChat()
            }}
        >
            {figChatLabel}
        </Link>
    )
}

/**
 * GenericErrorComponent displays a generic error message with an icon, title, subtitle, and a call-to-action button.
 * @param {Object} props - The component props.
 * @param {() => void} [props.onClick] - Optional click handler for the CTA button.
 * @returns {JSX.Element} The rendered GenericErrorComponent.
 */
function GenericErrorComponent({ onClick }: { onClick?: () => void }) {
    const { t } = useTranslation()
    const theme = useTheme()
    return (
        <Grid container display="flex" justifyContent="center" alignItems="center" textAlign="center" sx={{ margin: 'auto', px: { xs: 3, sm: 0 } }}>
            <Grid size={12} mb="32px">
                <img src={errorIcon} alt={t('common.alt.verificationFailed')} />
            </Grid>
            <Grid size={12}>
                <Typography variant="textSize1">{t('error.generic.title')}</Typography>
            </Grid>
            <Grid size={12} mt="16px" mb="16px">
                <Typography variant="textSize3Regular" color={theme.color.grey600}>
                    <Trans
                        i18nKey="error.generic.subtitle"
                        components={{
                            liveChatLink: <LiveChatLink screen="genericError" analyticsCallback={() => {}} />,
                            supportLink: <ContactFigLink screen="genericError" analyticsCallback={() => {}} />
                        }}
                    />
                </Typography>
            </Grid>
            <Grid size={12} mt="16px">
                <CTAButton buttonText={t('error.generic.buttonText')} onClick={onClick} variant="primary" />
            </Grid>
        </Grid>
    )
}

export default GenericErrorComponent
